import React, {
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState
} from "react";
import * as yup from "yup";
import { useFormikContext } from "formik";
import {
  format,
  parseISO,
  min,
  max,
  getDate,
  addMonths,
  addYears,
  subDays,
  subMonths,
  subYears,
  isSameDay,
  isSameWeek,
  isSameMonth,
  isSameQuarter,
  isBefore,
  isAfter,
  isValid,
  startOfToday,
  startOfDay,
  startOfWeek,
  startOfMonth,
  startOfQuarter,
  startOfYear,
  endOfWeek,
  endOfMonth,
  endOfYear,
  eachDayOfInterval,
  eachMonthOfInterval,
  eachQuarterOfInterval
} from "date-fns";
import { TimeScope, useTenantCreationDateQuery } from "generated/graphql";
import { useTenantId } from "polyrouter/SelectedTenantProvider";
import i18n, { useTranslation } from "utilities/i18n";
import { PolyIconButton } from "elements";
import { Form, Formik } from "collections";
import { Card } from "collections/Cards";
import "./TimeScopePicker.scss";
import { DATE_FORMAT } from "Constants";

const DateRangeContext = React.createContext({
  start: new Date(0),
  end: startOfToday()
});

const useTenantCreatedAt = () => {
  const id = useTenantId();
  const { data } = useTenantCreationDateQuery({ variables: { id } });
  const tenantCreatedAt = parseISO(data?.tenant.createdAt);
  return isValid(tenantCreatedAt) ? tenantCreatedAt : new Date(2019, 0, 1);
};

const useTimeScopePickerContext = () => {
  const dateRange = useContext(DateRangeContext);
  const { values, setFieldValue } = useFormikContext<{
    timeScope: TimeScope;
    timeStart: Date;
  }>();
  const tenantCreatedAt = useTenantCreatedAt();

  const setTimeScope = (timeScope: TimeScope) => {
    setFieldValue("timeScope", timeScope);
    setFieldValue(
      "timeStart",
      min([max([values.timeStart, dateRange.start]), dateRange.end])
    );
  };
  const setTimeStart = (timeStart: Date) => {
    setFieldValue(
      "timeStart",
      min([max([timeStart, dateRange.start]), dateRange.end])
    );
  };

  const checkDate = (date: Date) => {
    let isDateInRange = true;
    let message: string | undefined = undefined;

    if (isBefore(date, subDays(dateRange.start, 1))) {
      isDateInRange = false;
      message = i18n.t("DATE_BEFORE_ALLOWED_RANGE");
    }
    if (isAfter(date, dateRange.end)) {
      isDateInRange = false;
      message = i18n.t("DATE_AFTER_ALLOWED_RANGE");
    }
    if (isSameDay(date, tenantCreatedAt)) {
      message = i18n.t("TENANT_ESTABLISHED_ON_THIS_DATE");
    }

    return { isDateInRange, message };
  };

  return {
    ...values,
    dateRange,
    setTimeScope,
    setTimeStart,
    checkDate
  };
};

const generateClasses = (
  baseClasses: (string | boolean)[],
  isDateInRange: boolean,
  selected: boolean
): string => {
  return [
    ...baseClasses,
    "font-regular text-center",
    isDateInRange ? "clickable" : "disabled",
    selected && "highlighted"
  ]
    .filter(c => !!c)
    .join(" ");
};

const ScopeSelect: React.FC = () => {
  const { t } = useTranslation();
  const { timeScope, setTimeScope } = useTimeScopePickerContext();

  const options = [
    TimeScope.Day,
    TimeScope.Week,
    TimeScope.Month,
    TimeScope.Quarter,
    TimeScope.Year
  ];

  return (
    <div className="scope-select d-flex align-center p-0">
      {options.map(option => (
        <label
          key={`scope-${option}-label`}
          className={`scope-label font-regular text-center border-radius-4 clickable${
            option === timeScope ? " highlighted" : ""
          }`}
        >
          {t(option)}
          <input
            className="hidden"
            type="radio"
            checked={option === timeScope}
            onChange={() => setTimeScope(option)}
          />
        </label>
      ))}
    </div>
  );
};

const YearAndMonthSelect: React.FC = () => {
  const { t } = useTranslation();
  const {
    dateRange,
    timeScope,
    timeStart,
    setTimeStart
  } = useTimeScopePickerContext();

  const selectYearOnly =
    timeScope !== TimeScope.Day && timeScope !== TimeScope.Week;

  const backOption: Date = selectYearOnly
    ? subYears(timeStart, 1)
    : subMonths(timeStart, 1);
  const forwardOption: Date = selectYearOnly
    ? addYears(timeStart, 1)
    : addMonths(timeStart, 1);

  return (
    <div className="month-year-select d-flex align-center p-0">
      <PolyIconButton
        className="mr-15 ml-75"
        iconClassName="white clickable"
        icon="calendar_filled"
        title={t("CURRENT_DATE")}
        aria-label={t("CURRENT_DATE")}
        type="button"
        onClick={() => setTimeStart(startOfToday())}
      />
      <PolyIconButton
        iconClassName="white clickable"
        icon="filled_arrow_left"
        title={t("BACK")}
        aria-label={t("BACK")}
        type="button"
        disabled={
          selectYearOnly
            ? isBefore(backOption, startOfYear(dateRange.start))
            : isBefore(backOption, startOfMonth(dateRange.start))
        }
        onClick={() => setTimeStart(backOption)}
      />
      <label className="month-year-label font-regular text-center border-radius-4 mh-75 highlighted">
        {format(
          timeStart,
          selectYearOnly ? DATE_FORMAT.YEAR : DATE_FORMAT.MONTH_YEAR
        )}
      </label>
      <PolyIconButton
        iconClassName="white clickable"
        icon="filled_arrow_right"
        title={t("FORWARD")}
        aria-label={t("FORWARD")}
        type="button"
        disabled={
          selectYearOnly
            ? isAfter(forwardOption, endOfYear(dateRange.end))
            : isAfter(forwardOption, endOfMonth(dateRange.end))
        }
        onClick={() => setTimeStart(forwardOption)}
      />
    </div>
  );
};

const QuarterSelect: React.FC = () => {
  const { timeStart, setTimeStart, checkDate } = useTimeScopePickerContext();

  const options = eachQuarterOfInterval({
    start: startOfYear(timeStart),
    end: endOfYear(timeStart)
  });

  return (
    <>
      {options.map(option => {
        const { isDateInRange, message } = checkDate(option);
        const selected = isSameQuarter(option, timeStart);

        return (
          <label
            key={`quarter-${option}`}
            className={generateClasses(
              ["quarter-label border-radius-4"],
              isDateInRange,
              selected
            )}
            title={message}
          >
            {[option, addMonths(option, 1), addMonths(option, 2)]
              .map(month => format(month, DATE_FORMAT.MONTH_SHORT))
              .join(" - ")}
            <input
              className="hidden"
              type="radio"
              checked={selected}
              disabled={!isDateInRange}
              onChange={() => setTimeStart(option)}
            />
          </label>
        );
      })}
    </>
  );
};

const MonthSelect: React.FC = () => {
  const { timeStart, setTimeStart, checkDate } = useTimeScopePickerContext();

  const options = eachMonthOfInterval({
    start: startOfYear(timeStart),
    end: endOfYear(timeStart)
  });

  return (
    <>
      {options.map(option => {
        const { isDateInRange, message } = checkDate(option);
        const selected = isSameMonth(option, timeStart);

        return (
          <label
            key={`month-${option}`}
            className={generateClasses(
              ["month-label border-radius-4"],
              isDateInRange,
              selected
            )}
            title={message}
          >
            {format(option, DATE_FORMAT.MONTH)}
            <input
              className="hidden"
              type="radio"
              checked={selected}
              disabled={!isDateInRange}
              onChange={() => setTimeStart(option)}
            />
          </label>
        );
      })}
    </>
  );
};

const CalendarSelect: React.FC = () => {
  const {
    timeScope,
    timeStart,
    setTimeStart,
    checkDate
  } = useTimeScopePickerContext();

  const options = eachDayOfInterval({
    start: startOfWeek(startOfMonth(timeStart)),
    end: endOfWeek(endOfMonth(timeStart))
  });
  const header = options
    .slice(-7)
    .map(date => format(date, DATE_FORMAT.DAY_INITIAL));

  return (
    <div className="calendar grid">
      {header.map((day, i) => (
        <label
          key={`calendar-header-${i}`}
          className="calendar-header font-bold text-center"
        >
          {day}
        </label>
      ))}
      {options.map(option => {
        const valueToBeSet =
          timeScope === TimeScope.Week ? startOfWeek(option) : option;
        const { isDateInRange, message } = checkDate(valueToBeSet);
        const selected =
          timeScope === TimeScope.Week
            ? isSameWeek(option, timeStart)
            : isSameDay(option, timeStart);

        return (
          <label
            key={`calendar-${option}`}
            className={generateClasses(
              [
                timeScope.toLocaleLowerCase() + "-option",
                format(option, DATE_FORMAT.DAY).toLocaleLowerCase(),
                !isSameMonth(option, timeStart) && "adjacent-month"
              ],
              isDateInRange,
              selected
            )}
            title={message}
          >
            {getDate(option)}
            <input
              className="hidden"
              type="radio"
              checked={selected}
              disabled={!isDateInRange}
              onChange={() => setTimeStart(valueToBeSet)}
            />
          </label>
        );
      })}
    </div>
  );
};

export const TimeScopePicker: React.FC<{
  timeScope: TimeScope;
  timeStart: Date;
  onSubmit: (timeScope: TimeScope, timeStart: Date) => void;
  children: (togglePicker: () => void) => ReactNode;
}> = ({ timeScope, timeStart, onSubmit, children }) => {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    const handleClick = ({ target }: MouseEvent) => {
      if (!ref.current?.contains(target as HTMLElement)) {
        setShowForm(false);
      }
    };
    document.addEventListener("click", handleClick, true);
    return () => {
      document.removeEventListener("click", handleClick, true);
    };
  }, []);

  const tenantCreatedAt = useTenantCreatedAt();
  const dateRange = { start: tenantCreatedAt, end: startOfToday() };

  const TimeScopePickerSchema = yup.object().shape({
    timeScope: yup.mixed<TimeScope>().oneOf(Object.values(TimeScope)),
    timeStart: yup
      .date()
      .max(dateRange.end)
      .min(dateRange.start)
  });
  type TimeScopePickerValues = yup.InferType<typeof TimeScopePickerSchema>;

  const submitHandler = ({ timeScope, timeStart }: TimeScopePickerValues) => {
    const adjustedTimeStart = (() => {
      switch (timeScope) {
        case TimeScope.Week:
          return startOfWeek(timeStart);
        case TimeScope.Month:
          return startOfMonth(timeStart);
        case TimeScope.Quarter:
          return startOfQuarter(timeStart);
        case TimeScope.Year:
          return startOfYear(timeStart);
        default:
          return startOfDay(timeStart);
      }
    })();
    onSubmit(timeScope, adjustedTimeStart);
    setShowForm(false);
  };

  return (
    <DateRangeContext.Provider value={dateRange}>
      <div className="timescope-picker relative" ref={ref}>
        {showForm && (
          <Card className="absolute pv-15 ph-2 border-radius-4">
            <Formik
              name="TimeScopePicker"
              initialValues={{ timeScope, timeStart }}
              validationSchema={TimeScopePickerSchema}
              onSubmit={submitHandler}
            >
              {({ values, isValid, submitForm }) => (
                <Form>
                  <div className="d-flex align-center mb-2">
                    <h6 className="flex-1 font-xl p-0">{t("DATE_PICKER")}</h6>
                    <button
                      className="btn"
                      type="button"
                      onClick={() => setShowForm(false)}
                    >
                      {t("CANCEL")}
                    </button>
                    <button
                      className="btn btn-primary ml-1"
                      type="button"
                      disabled={!isValid}
                      onClick={() => submitForm()}
                    >
                      {t("APPLY")}
                    </button>
                  </div>
                  <div className="grid align-center mr-3">
                    <span className="p-0 mr-5 font-extra-bold">
                      {t("SCOPE")}
                    </span>
                    <ScopeSelect />
                    <span className="p-0 mr-5 font-extra-bold">
                      {t("RANGE")}
                    </span>
                    <YearAndMonthSelect />
                    <div className="col-1 offset-1 d-flex wrap p-0">
                      {(() => {
                        switch (values.timeScope) {
                          case TimeScope.Day:
                          case TimeScope.Week:
                            return <CalendarSelect />;
                          case TimeScope.Month:
                            return <MonthSelect />;
                          case TimeScope.Quarter:
                            return <QuarterSelect />;
                          default:
                            return null;
                        }
                      })()}
                    </div>
                  </div>
                </Form>
              )}
            </Formik>
          </Card>
        )}
      </div>
      {children(() => setShowForm(!showForm))}
    </DateRangeContext.Provider>
  );
};
